import { ElementRef, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { AllDayOutEvents } from '../all-day-out-events';
import { AppointmentEventsCarousel } from '../appointments-carousel';
import { HOURS_LANE_WIDTH, PROVIDER_COLUMN_GAP, SCROLLABLE_BUTTON_WIDTH } from '../constants';
import { useCalendarViewProps } from '../providers';
import { generateHourLabels, moveUnassignedProviderEvents, zIndex } from '../utils';
import { WorkingHoursIndicator } from '../working-hours-indicator';
import { BackgroundOverlay } from './background-overlay';
import { CalendarEventHourLines } from './calendar-event-hour-lines';
import { CalendarViewContainer } from './calendar-view-container';
import { CalendarViewMainWrapper } from './calendar-view-main-wrapper';
import { EventCards } from './event-cards';
import { EventTimelineWrapper } from './event-timeline-wrapper';
import { HoursOfTheDay } from './hours-of-the-day';
import { MultiOfficeProviders } from './multi-office-providers';
import { CalendarScrollButtons, ProviderScrollWrapper } from './scrollable-buttons';

const verticalLastEventGridSectionMarginRight = SCROLLABLE_BUTTON_WIDTH + PROVIDER_COLUMN_GAP;

export const CalendarViewMain = () => {
  const { startHour, endHour, data, id, NoProviderComponent, calendarDate } = useCalendarViewProps();
  const calendarViewWrapperRef = useRef<ElementRef<'article'>>(null);
  const headerRef = useRef<ElementRef<'section'>>(null);
  const hourLabels = generateHourLabels(startHour, endHour);
  const numberOfHours = hourLabels.length;
  const isTodaySelected = calendarDate === dayjs().format('MM/DD/YYYY');

  const { hasProviders, gridTemplateColumns } = useMemo(() => {
    const hasProviders = data?.some((office) => !!office?.providers?.length);
    // this is use to set the grid-template-columns for the vertical view for both top sticky provider list and the event grid
    const gridTemplateColumns = data
      ?.map((office) => (office?.providers?.length ? `${office.providers.length}fr` : ''))
      ?.join(' ');

    return { hasProviders, gridTemplateColumns };
  }, [data]);

  const baseTimezone = data?.[0]?.officeTimezone;

  return (
    <>
      {!hasProviders && NoProviderComponent && <NoProviderComponent />}
      <CalendarViewMainWrapper containerRef={calendarViewWrapperRef} id={id}>
        <motion.section ref={headerRef} css={headerContainerStyle}>
          <ProviderScrollWrapper containerRef={calendarViewWrapperRef}>
            <MultiOfficeProviders multiOfficeProviders={data} gridTemplateColumns={gridTemplateColumns} />
          </ProviderScrollWrapper>
          <AllDayOutEvents gridTemplateColumns={gridTemplateColumns} />
        </motion.section>
        <CalendarViewContainer numberOfHours={numberOfHours}>
          {/* // TODO: remove this background overlay as part of separate ticket as its no longer needed */}
          <BackgroundOverlay />

          <HoursOfTheDay
            hourLabels={hourLabels}
            timezone={baseTimezone}
            timezoneTextTopOffset={headerRef.current?.clientHeight}
          />
          <article
            css={[
              css`
                grid-column: 1/-1;
                grid-row: 1 / -1;
                padding-left: ${HOURS_LANE_WIDTH}px;
                padding-right: ${verticalLastEventGridSectionMarginRight}px;
                display: grid;
                grid-template-columns: ${gridTemplateColumns};
                grid-template-rows: auto;
                gap: ${PROVIDER_COLUMN_GAP}px;
              `,
            ]}
          >
            {data?.map((office, index) => {
              if (office.providers.length === 0) return null; // Do not show column if no providers

              // NOTE: This sorts the providers so that the unassigned provider is always at the end of the list
              const sortedProviders = moveUnassignedProviderEvents(office?.providers);
              return (
                <section
                  key={office.officeName + index}
                  css={{ height: '100%', display: 'flex', columnGap: `${PROVIDER_COLUMN_GAP}px`, position: 'relative' }}
                >
                  <WorkingHoursIndicator
                    workingHours={office.workingHours}
                    availabilityExceptions={office.availabilityExceptions}
                    hasAnyWorkingHoursSet={!!office.hasAnyWorkingHoursSet}
                  />
                  {sortedProviders.map((provider) => {
                    const officeEvents = office?.officeExceptions || [];
                    const providerEvents = provider.events.map((event) => ({
                      ...event,
                      providerId: provider.providerId,
                      providerName: provider.name,
                    }));
                    const allEvents = [...officeEvents, ...providerEvents];

                    return (
                      <EventTimelineWrapper
                        key={provider.providerId}
                        providerName={provider.name}
                        providerId={provider.providerId}
                        locationId={office.locationId}
                        calendarDateValue={office.calendarDateValue}
                        isRegisterResizeObserver={index === 0}
                      >
                        <WorkingHoursIndicator
                          workingHours={provider.workingHours}
                          availabilityExceptions={provider.availabilityExceptions}
                          hasAnyWorkingHoursSet={!!provider.hasAnyWorkingHoursSet}
                          isProvider
                        />
                        <EventCards
                          events={allEvents}
                          locationName={office.officeName}
                          calendarDate={office.calendarDateValue}
                          calendarViewWrapperRef={calendarViewWrapperRef}
                        />
                      </EventTimelineWrapper>
                    );
                  })}
                </section>
              );
            })}
          </article>

          <CalendarEventHourLines
            hourLabels={hourLabels}
            shouldShowTodayIndicator={hasProviders && isTodaySelected}
            shouldShowHoverIndicator={hasProviders}
            baseTimezone={baseTimezone}
          />
        </CalendarViewContainer>
      </CalendarViewMainWrapper>
      <CalendarScrollButtons containerRef={calendarViewWrapperRef} />
      <AppointmentEventsCarousel />
    </>
  );
};
const headerContainerStyle = css({
  position: 'sticky',
  top: 0,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows.floating,
  background: theme.colors.white,
  zIndex: zIndex.providerSection,
  transition: 'height 0.3s ease',
});
